import React, { useEffect, useRef, useState } from 'react';
import { stats } from '../data/mock';
import { ExternalLink } from 'lucide-react';

const Stats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-cycle through cards
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveCardIndex((prev) => (prev + 1) % 4);
    }, 2500);
    return () => clearInterval(interval);
  }, [isVisible]);

  // Filter stats to get 4 cards: Years + Role combined, Languages, Databases, Achievement
  const displayStats = [
    {
      type: 'experience',
      value: '5',
      label: 'Years of Experience',
      sublabel: 'Full-Stack Engineer'
    },
    {
      type: 'default',
      value: stats.find(s => s.label === 'Languages')?.value || '',
      label: 'Languages'
    },
    {
      type: 'default',
      value: stats.find(s => s.label === 'Databases')?.value || '',
      label: 'Databases'
    },
    {
      type: 'achievement',
      label: 'Achievements',
      achievements: [
        { text: 'ArcadeX - Gaming POS Platform', link: 'https://arcadex.charviam.com/' },
        { text: 'Digital Asset Broker Platform', link: 'https://github.com/manikantareddy12/digital-asset-broker-platform' }
      ]
    }
  ];

  return (
    <section
      id="stats"
      ref={sectionRef}
      className="py-12 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 transition-colors duration-500 relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-gradient-to-br from-gray-200/40 to-transparent dark:from-gray-700/30 rounded-full blur-3xl animate-morph" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-tl from-gray-300/30 to-transparent dark:from-gray-600/20 rounded-full blur-3xl animate-morph-reverse" />
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 relative z-10">
        {/* 4 cards in a single row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {displayStats.map((stat, index) => (
            <div
              key={stat.label}
              className={`group relative bg-white dark:bg-gray-900/60 p-6 rounded-xl border border-gray-200 dark:border-gray-800
                transition-all duration-700 hover:shadow-2xl dark:hover:shadow-2xl hover:-translate-y-3 hover:scale-105
                overflow-hidden hover:border-gray-400 dark:hover:border-gray-700
                ${isVisible
                  ? 'opacity-100 translate-y-0 scale-100'
                  : 'opacity-0 translate-y-12 scale-95'
                }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Animated rotating border - shows on hover or when active */}
              <div className={`absolute inset-0 transition-opacity duration-500 ${index === activeCardIndex ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}>
                <div className="absolute inset-[-2px] animate-border-spin">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-400/60 to-transparent dark:via-gray-600/70 rounded-xl" />
                </div>
                <div className="absolute inset-[2px] bg-white dark:bg-gray-900/60 rounded-xl" />
              </div>

              {/* Content - centered with label at bottom */}
              <div className="relative z-10 flex flex-col h-full min-h-[150px]">
                {/* Experience card - special layout */}
                {stat.type === 'experience' && (
                  <>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <p className="text-[32px] md:text-[44px] font-light text-gray-700 dark:text-gray-200 mb-2 tracking-tight group-hover:scale-110 transition-all duration-500">
                        {stat.value}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-medium group-hover:text-gray-800 dark:group-hover:text-gray-300 transition-colors">
                        {stat.sublabel}
                      </p>
                    </div>
                    <div className="text-center mt-auto pt-3 border-t border-gray-200 dark:border-gray-800">
                      <p className="text-xs text-gray-500 dark:text-gray-500 font-light uppercase tracking-wider">
                        {stat.label}
                      </p>
                      {/* Mini animated underline */}
                      <div className="relative h-[2px] w-12 mx-auto mt-1 overflow-hidden bg-gray-200 dark:bg-gray-800 rounded-full">
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-400 via-gray-600 to-gray-400 dark:from-gray-600 dark:via-gray-400 dark:to-gray-600
                                        group-hover:animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ width: '50%' }} />
                      </div>
                    </div>
                  </>
                )}

                {/* Achievement card with link icons */}
                {stat.type === 'achievement' && (
                  <>
                    <div className="flex-1 flex flex-col items-center justify-center gap-3">
                      {stat.achievements.map((achievement, i) => (
                        <p key={i} className="text-sm font-light text-black dark:text-gray-200 leading-snug text-center flex items-center gap-1.5">
                          {achievement.text}
                          <a
                            href={achievement.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-black dark:hover:text-white transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink size={12} />
                          </a>
                        </p>
                      ))}
                    </div>
                    <div className="text-center mt-auto pt-3 border-t border-gray-200 dark:border-gray-800">
                      <p className="text-xs text-gray-500 dark:text-gray-500 font-light uppercase tracking-wider">
                        {stat.label}
                      </p>
                      {/* Mini animated underline */}
                      <div className="relative h-[2px] w-12 mx-auto mt-1 overflow-hidden bg-gray-200 dark:bg-gray-800 rounded-full">
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-400 via-gray-600 to-gray-400 dark:from-gray-600 dark:via-gray-400 dark:to-gray-600
                                        group-hover:animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ width: '50%' }} />
                      </div>
                    </div>
                  </>
                )}

                {/* Default cards */}
                {stat.type === 'default' && (
                  <>
                    <div className="flex-1 flex items-center justify-center">
                      <p className="text-xs md:text-sm font-light text-black dark:text-gray-200 leading-snug line-clamp-3 text-center group-hover:scale-105 transition-all duration-500">
                        {stat.value}
                      </p>
                    </div>
                    <div className="text-center mt-auto pt-3 border-t border-gray-200 dark:border-gray-800">
                      <p className="text-xs text-gray-500 dark:text-gray-500 font-light uppercase tracking-wider">
                        {stat.label}
                      </p>
                      {/* Mini animated underline */}
                      <div className="relative h-[2px] w-12 mx-auto mt-1 overflow-hidden bg-gray-200 dark:bg-gray-800 rounded-full">
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-400 via-gray-600 to-gray-400 dark:from-gray-600 dark:via-gray-400 dark:to-gray-600
                                        group-hover:animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ width: '50%' }} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
